import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TextArea } from '@anyroad/text-area';
import { Icon } from '@anyroad/icon';

import { Theme } from 'ui_system/theme';

import {
  Container,
  Wrapper,
  HeaderRow,
  Header,
  Toggle,
  Hint,
  HintIcon,
  HintText,
  Reveal,
  TextAreaWrapper,
} from './styles';

type FormLikeOnChange = (fieldName: string, value: any) => void;

type MessageGuideProps = {
  guideName: string;
  showHint?: boolean;
  borderless?: boolean;
  onChange: FormLikeOnChange;
};

export const MessageGuide = ({
  guideName,
  showHint,
  borderless,
  onChange,
}: MessageGuideProps) => {
  const { formatMessage } = useIntl();
  const { isPhone } = Theme.mobile;
  const [showMessageInput, setShowMessageInput] = useState<boolean>(!isPhone);

  const placeholder = showHint
    ? formatMessage({
        defaultMessage: 'Enter your message here',
        description: 'Placeholder for message to guide textarea',
      })
    : formatMessage({
        defaultMessage: 'Have a special request, or just want to say Hi?',
        description: 'Placeholder for optional message to guide',
      });

  const toggleMessageInput = useCallback(() => {
    setShowMessageInput((prev) => !prev);
  }, []);

  const hintText = showHint ? (
    <FormattedMessage
      defaultMessage="Requesting a pickup or drop off? Please include your place of stay so appropriate arrangements can be made."
      description="Hint under message textarea prompting guests to include pickup details"
    />
  ) : null;

  return (
    <Container borderless={borderless} className="CheckoutSection">
      <Wrapper data-testid="checkout-message" id="checkout-message">
        <HeaderRow isPhone={isPhone}>
          {isPhone && (
            <Toggle
              role="button"
              aria-label='Expands "send a message"'
              aria-expanded={showMessageInput}
              onClick={toggleMessageInput}
              className={`icon anyfont-icon af-promo-${
                showMessageInput ? 'minus' : 'plus'
              }`}
            />
          )}
          <Header isPhone={isPhone}>
            <FormattedMessage
              defaultMessage="Send a message to {guideName} (Optional)"
              description="Checkout section title for messaging the guide"
              values={{
                guideName,
              }}
            />
          </Header>
        </HeaderRow>
        {showHint && (
          <Hint>
            <HintIcon>
              <Icon name="Lightbulb" className="brand-color" />
            </HintIcon>
            <HintText>{hintText}</HintText>
          </Hint>
        )}
        <Reveal show={showMessageInput}>
          <TextAreaWrapper>
            <TextArea
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                onChange('message', e.currentTarget.value)
              }
              placeholder={placeholder}
              aria-label="Send a message"
              aria-labelledby="checkout-message"
            />
          </TextAreaWrapper>
        </Reveal>
      </Wrapper>
    </Container>
  );
};

export default MessageGuide;
